import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Users, Pencil, LogOut, Clock, ShieldX, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AppearanceMenu } from "@/components/AppearanceMenu";
import { Logo } from "@/components/Logo";
import { Loading, EmptyState } from "@/components/States";
import { JobEditor, Applicants, REVIEW_STYLE, REVIEW_LABEL } from "@/components/admin/AdminJobs";
import { JobMeta } from "@/pages/Jobs";
import { useAuth } from "@/context/AuthContext";
import { API, api, fetchSubjects, formatError } from "@/lib/api";

const EMP_STATUSES = ["shortlisted", "rejected", "hired"];

function StatusBanner({ user }) {
  if (user.employer_status === "approved") return null;
  const rejected = user.employer_status === "rejected";
  const Icon = rejected ? ShieldX : Clock;
  return (
    <div data-testid={`employer-banner-${user.employer_status}`} className={`mb-6 flex items-start gap-3 rounded-2xl border p-5 ${rejected ? "border-red-200 bg-red-50 text-red-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
      <Icon className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="text-sm">
        <p className="font-semibold">{rejected ? "Your employer account wasn't approved" : "Your account is awaiting IDDCR approval"}</p>
        <p className="mt-1">{rejected ? (user.review_note || "Please contact IDDCR if you think this is a mistake.") : "We'll email you once it's approved. Until then you can complete your company profile."}</p>
      </div>
    </div>
  );
}

function JobItem({ j, onEdit, onReload, approved }) {
  const [show, setShow] = useState(false);
  const toggle = async (open) => {
    try { await api.patch(`/employer/jobs/${j.id}/open`, { open }); toast.success(open ? "Job reopened" : "Job closed"); onReload(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid={`employer-job-${j.id}`} className="rounded-xl border border-line bg-surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-ink">{j.title}
            <span data-testid={`employer-job-status-${j.id}`} className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-medium ${REVIEW_STYLE[j.review_status]}`}>{REVIEW_LABEL[j.review_status]}</span>
            {j.review_status === "approved" && <span className="ml-1 rounded-full bg-elevated px-2 py-0.5 text-[11px] font-medium text-ink-2">{j.live ? "Live" : j.open ? "Deadline passed" : "Closed"}</span>}</p>
          <JobMeta j={j} />
          {j.review_status === "rejected" && j.review_reason && <p data-testid={`employer-job-reason-${j.id}`} className="mt-1 text-xs text-red-700">IDDCR feedback: {j.review_reason}</p>}
        </div>
        {approved && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-ink-3"><Switch data-testid={`employer-job-open-${j.id}`} checked={j.open} onCheckedChange={toggle} />{j.open ? "Open" : "Closed"}</label>
            <Button size="sm" variant="outline" data-testid={`employer-job-edit-${j.id}`} onClick={() => onEdit(j)} className="rounded-full"><Pencil className="mr-1.5 h-3.5 w-3.5" />Edit</Button>
            <Button size="sm" variant="outline" data-testid={`employer-job-applicants-btn-${j.id}`} onClick={() => setShow(!show)} className="rounded-full"><Users className="mr-1.5 h-3.5 w-3.5" />{j.applicants} applicant{j.applicants === 1 ? "" : "s"}</Button>
          </div>
        )}
      </div>
      {show && <Applicants job={j} base="/employer" statuses={EMP_STATUSES} />}
    </div>
  );
}

function LogoUpload({ user, setUser }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const logo = user.company?.logo_id;
  const pick = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return toast.error("Please choose a JPG, PNG or WebP logo");
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo must be 2 MB or smaller");
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { setUser((await api.post("/employer/logo", fd)).data); toast.success("Logo updated"); } catch (err) { toast.error(formatError(err)); }
    setBusy(false);
  };
  const remove = async () => { try { setUser((await api.delete("/employer/logo")).data); toast.success("Logo removed"); } catch (err) { toast.error(formatError(err)); } };
  return (
    <div data-testid="employer-logo-upload" className="flex items-center gap-4">
      <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-line bg-surface p-1">
        {logo ? <img data-testid="employer-logo-preview" src={`${API}/public/logos/${logo}`} alt="Company logo" className="max-h-full max-w-full object-contain" /> : <Building2 className="h-7 w-7 text-slate-300" />}
      </div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="employer-logo-input" onChange={pick} />
      <Button size="sm" variant="outline" disabled={busy} data-testid="employer-logo-upload-btn" onClick={() => ref.current?.click()} className="rounded-full">{busy ? "Uploading..." : logo ? "Replace logo" : "Upload logo"}</Button>
      {logo && <Button size="sm" variant="ghost" data-testid="employer-logo-remove-btn" onClick={remove} className="rounded-full text-red-600">Remove</Button>}
      <span className="text-xs text-ink-3">JPG, PNG or WebP, up to 2 MB</span>
    </div>
  );
}

function CompanyProfile({ user, setUser }) {
  const [f, setF] = useState({ company_name: user.company?.name || "", website: user.company?.website || "", contact_name: user.name, description: user.company?.description || "" });
  const save = async () => {
    try { setUser((await api.put("/employer/profile", f)).data); toast.success("Company profile saved"); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid="employer-profile" className="flex max-w-xl flex-col gap-3 rounded-2xl border border-line bg-surface p-6">
      <Input data-testid="employer-profile-company" placeholder="Company name" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} />
      <Input data-testid="employer-profile-website" placeholder="https://company.com" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />
      <Input data-testid="employer-profile-contact" placeholder="Contact name" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} />
      <div>
        <p className="mb-1 flex justify-between text-xs font-medium text-ink-2"><span>Company description (shown on your public page)</span><span data-testid="employer-profile-description-count" className="font-normal text-ink-3">{f.description.length}/1000</span></p>
        <Textarea data-testid="employer-profile-description" rows={4} maxLength={1000} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="What your company does, therapeutic areas, culture..." />
      </div>
      <LogoUpload user={user} setUser={setUser} />
      {user.employer_status === "approved" && user.company?.slug && <a data-testid="employer-company-page-link" href={`/company/${user.company.slug}`} target="_blank" rel="noreferrer" className="text-sm font-medium text-aqua hover:underline">View public company page: /company/{user.company.slug}</a>}
      <p className="text-xs text-ink-3">Login email: {user.email}</p>
      <Button data-testid="employer-profile-save" onClick={save} className="self-start rounded-full bg-solid">Save profile</Button>
    </div>
  );
}

export default function EmployerDashboard() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState(null);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const approved = user.employer_status === "approved";
  const load = () => api.get("/employer/jobs").then((r) => setJobs(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); api.get("/courses").then((r) => setCourses(r.data)); fetchSubjects().then(setSubjects); }, []);
  const edit = (j) => { setEditing(j); setTab("post"); };
  return (
    <div className="min-h-screen bg-canvas">
      <header className="flex items-center justify-between border-b border-line bg-surface px-4 py-4 sm:px-8">
        <Logo to="/employer" className="h-9" />
        <div className="flex items-center gap-3">
          <AppearanceMenu />
          <span data-testid="employer-company-name" className="hidden items-center gap-1.5 text-sm font-medium text-ink sm:inline-flex"><Building2 className="h-4 w-4 text-aqua" />{user.company?.name}</span>
          <Button size="sm" variant="ghost" data-testid="employer-logout-btn" onClick={async () => { await logout(); nav("/"); }} className="rounded-full"><LogOut className="mr-1.5 h-4 w-4" />Log out</Button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-aqua">Employer dashboard</p>
        <h1 className="mb-6 mt-2 text-4xl font-extrabold text-ink">Hire IDDCR-certified talent</h1>
        <StatusBanner user={user} />
        <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v !== "post") setEditing(null); }}>
          <TabsList className="mb-6">
            <TabsTrigger value="jobs" data-testid="employer-tab-jobs">My jobs</TabsTrigger>
            <TabsTrigger value="post" data-testid="employer-tab-post" disabled={!approved}>{editing ? "Edit job" : "Post a job"}</TabsTrigger>
            <TabsTrigger value="profile" data-testid="employer-tab-profile">Company profile</TabsTrigger>
          </TabsList>
          <TabsContent value="jobs">
            {!jobs ? <Loading /> : jobs.length === 0 ? (
              <EmptyState testId="employer-jobs-empty" title="No jobs yet" text={approved ? "Post your first clinical research role. IDDCR reviews it before it goes live." : "You can post jobs once IDDCR approves your account."} action={approved ? "Post a job" : undefined} onAction={() => setTab("post")} />
            ) : (
              <div data-testid="employer-jobs-list" className="flex flex-col gap-3">{jobs.map((j) => <JobItem key={j.id} j={j} onEdit={edit} onReload={load} approved={approved} />)}</div>
            )}
          </TabsContent>
          <TabsContent value="post">
            {approved && <JobEditor key={editing?.id || "new"} job={editing} courses={courses} subjects={subjects} base="/employer/jobs" employer
              onCancel={() => { setEditing(null); setTab("jobs"); }} onSaved={() => { setEditing(null); setTab("jobs"); load(); }} />}
          </TabsContent>
          <TabsContent value="profile"><CompanyProfile user={user} setUser={setUser} /></TabsContent>
        </Tabs>
        {approved && tab === "jobs" && jobs?.length > 0 && <Button data-testid="employer-new-job-btn" onClick={() => setTab("post")} className="mt-6 rounded-full bg-solid"><Plus className="mr-1.5 h-4 w-4" />Post a job</Button>}
      </main>
    </div>
  );
}
